import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canAccessPortalPage, canAccessCategory, getAccessiblePortalPages } from '../services/portalAccessService';
import portalAccessEventService from '../services/portalAccessEventService';

/**
 * Custom hook for portal access management
 * Provides easy access to portal page permissions
 */
export const usePortalAccess = () => {
  const { currentUser } = useAuth();
  const [accessiblePages, setAccessiblePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryAccess, setCategoryAccess] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isPortalLocked, setIsPortalLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState('');
  const [isPendingStart, setIsPendingStart] = useState(false);
  const [programStartDate, setProgramStartDate] = useState(null);
  const [portalAccessConfigured, setPortalAccessConfigured] = useState(false);

  const loadAccessData = async () => {
    if (!currentUser) {
      setAccessiblePages([]);
      setCategoryAccess({});
      setIsPortalLocked(false);
      setLockMessage('');
      setIsPendingStart(false);
      setProgramStartDate(null);
      setPortalAccessConfigured(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('[Portal Access] Loading access data ONCE for user:', currentUser.uid);
      
      // Get account data directly instead of checking each page individually
      const { getClientAccountData } = await import('../services/portalAccessService');
      const accountData = await getClientAccountData(currentUser.uid);
      
      // Portal lock status - when locked, client only sees Programs (needs to purchase)
      setIsPortalLocked(accountData?.lockStatus === 'locked');
      setLockMessage(accountData?.lockMessage || '');

      // Pending start: clients whose program start date is still in the future wait on a
      // holding page and can't use the portal until that day arrives.
      let pendingStart = false;
      let startDateValue = null;
      if (accountData?.type === 'Clients') {
        const rawStart = accountData.program?.startDate || accountData.contractStart;
        const startDate = rawStart?.toDate ? rawStart.toDate() : rawStart ? new Date(rawStart) : null;
        if (startDate && !isNaN(startDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const startDay = new Date(startDate);
          startDay.setHours(0, 0, 0, 0);
          if (startDay > today) {
            pendingStart = true;
            startDateValue = startDay;
          }
        }
      }
      setIsPendingStart(pendingStart);
      setProgramStartDate(startDateValue);

      // Whether anyone has actually set this account's Access tab. Once they
      // have, those toggles are authoritative — including for the handful of
      // pages the locked portal otherwise always shows.
      setPortalAccessConfigured(Object.keys(accountData?.portalAccess || {}).length > 0);

      if (accountData && accountData.portalAccess) {
        const portalAccess = accountData.portalAccess;
        console.log('[Portal Access] Account portal access settings:', portalAccess);
        
        // Build accessible pages list from portal access settings
        const { PORTAL_PAGE_MAPPING, PORTAL_PAGE_TOGGLE_KEYS } = await import('../services/portalAccessService');
        const accessiblePages = [];
        
        for (const [route, accessKey] of Object.entries(PORTAL_PAGE_MAPPING)) {
          const isToggleable = PORTAL_PAGE_TOGGLE_KEYS.includes(accessKey);
          const hasAccess = isToggleable ? portalAccess[accessKey] === true : true;
          if (hasAccess) {
            accessiblePages.push(route);
          }
        }
        
        setAccessiblePages(accessiblePages);
        console.log('[Portal Access] Built accessible pages from settings:', accessiblePages);

        // Build category access from portal access settings
        const categories = ['sales', 'operations', 'technology', 'finance', 'marketing', 'hr'];
        const categoryAccessData = {};
        
        for (const category of categories) {
          categoryAccessData[category] = portalAccess[category] === true;
        }
        
        setCategoryAccess(categoryAccessData);
        console.log('[Portal Access] Built category access from settings:', categoryAccessData);
      } else {
        console.log('[Portal Access] No portal access settings found, using defaults');
        // Mirror canAccessPortalPage: pages without an Access-tab toggle (SOPs,
        // KPIs, job descriptions, goals…) are always full access; toggleable
        // pages fall back to their defaults until the Access tab is configured.
        const { PORTAL_PAGE_MAPPING, PORTAL_PAGE_TOGGLE_KEYS } = await import('../services/portalAccessService');
        const defaultToggleAccess = {
          courses: true, calendar: true, community: true, tasks: true, events: true
        };
        const defaultPages = Object.entries(PORTAL_PAGE_MAPPING)
          .filter(([, accessKey]) =>
            !PORTAL_PAGE_TOGGLE_KEYS.includes(accessKey) || defaultToggleAccess[accessKey] === true
          )
          .map(([route]) => route);
        setAccessiblePages(defaultPages);
        setCategoryAccess({});
      }
    } catch (error) {
      console.error('Error loading portal access data:', error);
      setAccessiblePages([]);
      setCategoryAccess({});
      setIsPortalLocked(false);
      setLockMessage('');
      setIsPendingStart(false);
      setProgramStartDate(null);
      setPortalAccessConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccessData();
  }, [currentUser, refreshTrigger]);

  // Listen for portal access change events
  useEffect(() => {
    const unsubscribe = portalAccessEventService.subscribe((userId) => {
      // Refresh if it's for current user or if userId is 'current'
      if (userId === 'current' || userId === currentUser?.uid) {
        console.log('Portal access changed, refreshing data...');
        refreshAccessData();
      }
    });

    return unsubscribe;
  }, [currentUser]);

  /**
   * Check if user can access a specific page (async - for compatibility)
   * @param {string} route - The route to check
   * @returns {Promise<boolean>}
   */
  const canAccessPage = async (route) => {
    if (!currentUser) return false;
    // Use cached data if available, otherwise fetch
    if (accessiblePages.length > 0) {
      return accessiblePages.includes(route);
    }
    return await canAccessPortalPage(currentUser.uid, route);
  };

  /**
   * Check if user can access a category
   * @param {string} category - The category to check
   * @returns {boolean}
   */
  const canAccessCategoryPage = (category) => {
    return categoryAccess[category] === true;
  };

  /**
   * Check if a page is accessible (synchronous check from loaded data)
   * @param {string} route - The route to check
   * @returns {boolean}
   */
  const isPageAccessible = (route) => {
    return accessiblePages.includes(route);
  };

  /**
   * A locked portal falls back to a fixed always-visible set (Dashboard,
   * Roadmap) so the client can still see what they're buying. But once the
   * Access tab has been configured for the account, an off toggle has to win —
   * otherwise switching Roadmap off in the CRM silently does nothing.
   * Only meaningful while isPortalLocked; harmless elsewhere.
   * @param {string} route - The mapped portal route to check
   * @returns {boolean}
   */
  const isLockedPageHidden = (route) => {
    return portalAccessConfigured && !accessiblePages.includes(route);
  };

  /**
   * Refresh portal access data
   * Call this when you know the access settings have changed
   */
  const refreshAccessData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return {
    accessiblePages,
    categoryAccess,
    loading,
    canAccessPage,
    canAccessCategoryPage,
    isPageAccessible,
    isLockedPageHidden,
    portalAccessConfigured,
    refreshAccessData,
    isPortalLocked,
    lockMessage,
    isPendingStart,
    programStartDate
  };
};
